import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import camelcaseKeys from 'camelcase-keys';
import ShareRoundedIcon from '@mui/icons-material/ShareRounded';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Typography, Avatar, IconButton } from '@mui/material';
import {
  ChevronRightRounded,
  FiberManualRecordRounded,
  KeyboardArrowDownRounded,
  KeyboardArrowUpRounded,
  OfflineBoltRounded,
} from '@mui/icons-material';
import {
  getCategoryLabel,
  getFrameVoiceUrl,
  getVoiceStatusLabel,
  getVoiceSubStatusLabel,
} from '@src/services/voiceCloning';
import {
  VOICE_STATUS,
  VOICE_STATUS_COLORS,
  SUB_STATUS,
  REASON_VOICE_STATUS,
  VOICE_NOT_USED,
  VOICE_COMPLETED,
  VOICE_CAN_USE,
  STEP_VOICE_DETAIL,
  SUB_STATUS_COLORS,
  VOICE_CLONE_TYPE,
} from '@src/constants/voiceCloning';
import actions from '@src/redux/actions';
import route from '@src/constants/route';
import { LANDING_PAGE_URL } from '@src/configs';
import useFeatureFlags from '@src/hooks/useFeatureFlags';
import { FEATURE_KEYS } from '@src/configs/featureKeys';
import useIpLocation from '@src/hooks/useLocation';
import { checkUserHasProvidedEmail } from '@src/services/email';
import ProvideEmailToUseFeatureDialog from '@src/containers/Layout/RequireProvideEmailDialog/ProvideEmailToUseFeatureDialog';
import CommonAvatar from '@src/new-components/Avatar';
import { COLOR } from '@src/styles/color';
import Tooltip from '@src/components/Tooltip';
import verifiedIcon from '@src/assets/icons/verified-rounded.svg';
import CommonButton from '@src/new-components/Button';
import { StyledVoicesInformation, StyledChip } from './index.style';
import ViewReason from './ViewReason';
import ViewReasonNew from './ViewReasonNew';
import WarningDialog from './WarningDialog';
import VoiceDetail from './VoiceDetail';
import InfoTooltipStatusVoice from './InfoTooltipStatusVoice';
import WaitingForRemovalTag from './WaitingForRemovalTag';
import VoiceItemCardNew from './VoiceItemCardNew';

const VoiceInformation = ({
  id,
  avatar,
  name,
  category = {},
  status,
  province = {},
  gender,
  subStatus,
  note,
  voiceInfo,
  checkUserCanUseVoiceCloning,
  setOpenVoiceDetail,
  setReasonContent,
  setOpenViewReason,
  setViewReasonId,
  setIsShowContact,
  setViewReasonVoiceStatus,
  setViewReasonVoiceInfo,
  onContinueRecord,
  onShowUpgradePackage,
  handleDeleteVoice,
  isInstantVoiceCloning = true,
  setStepVoiceDetail,
  onOpenProvideEmailDialog,
  onContinueUploadVoice,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const { ip } = useIpLocation();

  const newVoiceCloningEnabled = getFeatureValue(
    FEATURE_KEYS.NEW_VOICE_CLONING,
    {
      userId: user?.id,
      email: user?.email,
      phoneNumber: user?.phoneNumber,
      ip,
    },
  );

  const hasProvidedEmail = checkUserHasProvidedEmail(user);

  const renderVoiceStatus = (voiceStatus) => {
    const label = getVoiceStatusLabel(voiceStatus);
    const color = VOICE_STATUS_COLORS[voiceStatus];
    if (
      voiceInfo?.type === VOICE_CLONE_TYPE.FINETUNE_LONG_AUDIO &&
      voiceStatus === VOICE_STATUS.RECORDING
    ) {
      return <StyledChip color={color}>{t('gatheringAudio')}</StyledChip>;
    }

    if (!color || !label) return '';
    return <StyledChip color={color}>{t(label)}</StyledChip>;
  };

  const renderVoiceSubStatus = ({ voiceStatus, voiceSubStatus, discardAt }) => {
    const enableSubStatusColor = [
      VOICE_STATUS.PRIVATE,
      VOICE_STATUS.PUBLIC,
    ].includes(voiceStatus);

    if (!enableSubStatusColor || !voiceSubStatus) return '';

    const label = getVoiceSubStatusLabel(voiceSubStatus);
    const color = SUB_STATUS_COLORS?.[voiceSubStatus];

    if (!color || !label) return '';

    if (voiceSubStatus === SUB_STATUS.WAITING_FOR_REMOVAL)
      return (
        <WaitingForRemovalTag
          subStatus={voiceSubStatus}
          discardAt={discardAt}
        />
      );

    return <StyledChip color={color}>{t(label)}</StyledChip>;
  };

  const onClickShareAndEarn = (e) => {
    e.stopPropagation();
    if (!hasProvidedEmail) {
      onOpenProvideEmailDialog(true);
      return;
    }
    dispatch(actions.voiceVoiceCloning.addVoice(voiceInfo));
    setStepVoiceDetail(STEP_VOICE_DETAIL.FORM_SHARE_AND_EARN);
    setOpenVoiceDetail(true);
  };

  const onClickRedirectVoice = () => {
    if (!checkUserCanUseVoiceCloning) {
      onShowUpgradePackage();
      return;
    }
    setStepVoiceDetail(STEP_VOICE_DETAIL.VOICE_DETAIL);
    dispatch(actions.voiceVoiceCloning.addVoice(voiceInfo));
    setOpenVoiceDetail(true);
  };

  const onClickShowReason = (event) => {
    event.stopPropagation();
    setReasonContent(note);
    setOpenViewReason(true);
    setViewReasonId(id);
    setIsShowContact(VOICE_NOT_USED.includes(status));
    setViewReasonVoiceStatus(status);
    setViewReasonVoiceInfo(voiceInfo);
  };

  const continueRecording = (e) => {
    e.stopPropagation();
    onContinueRecord(id);
  };

  const onClickDeleteVoice = (e) => {
    e.stopPropagation();
    handleDeleteVoice(voiceInfo);
  };

  const onClickContinue = (e) => {
    e.stopPropagation();
    if (onContinueUploadVoice) {
      onContinueUploadVoice(voiceInfo);
    }
  };
  const onClickUseVoice = () => {
    if (!checkUserCanUseVoiceCloning) {
      onShowUpgradePackage();
      return;
    }
    dispatch(actions.synthesisRequest.updateVoice(voiceInfo));
    history.push(route.TTS);
  };

  const voiceInfoButtonAction = () => {
    const isVoiceDeletable = [
      VOICE_STATUS.RECORDING,
      VOICE_STATUS.REJECTED,
      VOICE_STATUS.READY,
      VOICE_STATUS.USING,
      VOICE_STATUS.FAILED,
      VOICE_STATUS.SUSPENDED,
    ].includes(status);

    const shouldShowDeleteVoiceOption =
      !checkUserCanUseVoiceCloning && isVoiceDeletable;
    const hasRecording = status === VOICE_STATUS.RECORDING;
    const hasVoiceWithUpload =
      voiceInfo?.type === VOICE_CLONE_TYPE.FINETUNE_LONG_AUDIO;

    if (hasVoiceWithUpload && hasRecording) {
      return (
        <Box>
          <CommonButton
            customStyle={{
              textTransform: 'none',
              height: '32px',
              borderRadius: '8px',
            }}
            variant="outlineSmall"
            textProps={{
              variantText: 'body-14-medium',
              color: COLOR.darkBlue,
            }}
            onClick={onClickContinue}
          >
            {t('continue')}
          </CommonButton>
        </Box>
      );
    }
    if (shouldShowDeleteVoiceOption) {
      return (
        <Box className="view-reason" onClick={onClickDeleteVoice}>
          <Box className="view-reason-text">{t('deleteVoice')}</Box>
        </Box>
      );
    }
    if (REASON_VOICE_STATUS.includes(status))
      return (
        <Box className="view-reason" onClick={onClickShowReason}>
          <Box className="view-reason-text">{t('viewReason')}</Box>
        </Box>
      );

    const canUseVoice = VOICE_CAN_USE.includes(status);

    const isShareVoice = status === VOICE_STATUS.PRIVATE && !subStatus;
    const showBtnShare = status === VOICE_STATUS.PUBLIC;

    const handleRedirectVoiceProfile = () => {
      window.open(`${LANDING_PAGE_URL}/voices/${voiceInfo?.code}`, '_blank');
    };

    if (canUseVoice && !isMobile) {
      return (
        <Box className="use-and-share-voice">
          <CommonButton
            customStyle={{
              textTransform: 'none',
              height: '32px',
              borderRadius: '8px',
            }}
            variant="outlineSmall"
            textProps={{
              variantText: 'body-14-medium',
              color: COLOR.darkBlue,
            }}
            onClick={onClickUseVoice}
          >
            {t('voiceUse')}
          </CommonButton>
          {showBtnShare && (
            <Tooltip placement="top" title={t('shareVoiceProfilePage')} arrow>
              <div>
                <CommonButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRedirectVoiceProfile();
                  }}
                  customStyle={{
                    textTransform: 'none',
                    height: '32px',
                    width: '32px',
                    minWidth: '32px',
                    borderRadius: '8px',
                  }}
                  variant="outlineSmall"
                >
                  <ShareRoundedIcon
                    sx={{ color: COLOR.navyBlue, fontSize: '20px' }}
                  />
                </CommonButton>
              </div>
            </Tooltip>
          )}
          {isShareVoice && !isInstantVoiceCloning && (
            <CommonButton
              customStyle={{
                textTransform: 'none',
                height: '32px',
                borderRadius: '8px',
              }}
              variant="containedSmall"
              textProps={{
                variantText: 'body-14-medium',
                color: COLOR.darkBlue,
              }}
              onClick={onClickShareAndEarn}
            >
              {t('shareAndEarn')}
            </CommonButton>
          )}
        </Box>
      );
    }

    if ([VOICE_STATUS.RECORDING, VOICE_STATUS.REJECTED].includes(status))
      return (
        <Box className="view-reason" onClick={continueRecording}>
          <Box className="view-reason-text">{t('continueRecording')}</Box>
        </Box>
      );

    return <></>;
  };

  const renderBadgeIcon = () => {
    if (isInstantVoiceCloning) {
      return (
        <OfflineBoltRounded
          sx={{
            color: COLOR.darkBlue,
            backgroundColor: COLOR.white,
            borderRadius: '50%',
          }}
        />
      );
    }
    return <img src={verifiedIcon} alt="badge" className="verified-icon" />;
  };

  return (
    <Box
      className="voice"
      onClick={onClickRedirectVoice}
      id={`voice-${voiceInfo?.id}`}
    >
      {newVoiceCloningEnabled ? (
        <CommonAvatar
          avatar={avatar}
          badgeIcon={renderBadgeIcon()}
          size={64}
          className="badge-avatar"
          frameImageUrl={getFrameVoiceUrl(voiceInfo)}
        />
      ) : (
        <Avatar src={avatar} alt="avatar" className="avatar" />
      )}
      <Box className="voice-info">
        <Box className="name-info">
          <Box className="name-info-left">
            <Typography className="name">{name}</Typography>
            {province?.id && (
              <>
                <FiberManualRecordRounded className="name icon-size-6" />
                <Typography className="name">{province?.name}</Typography>
              </>
            )}

            {[VOICE_STATUS.PRIVATE, VOICE_STATUS.PUBLIC].includes(status) && (
              <>
                <FiberManualRecordRounded className="name icon-size-6" />
                <InfoTooltipStatusVoice status={status} />
                {renderVoiceSubStatus({
                  voiceStatus: status,
                  voiceSubStatus: subStatus,
                  discardAt: voiceInfo?.discardAt,
                })}
              </>
            )}
          </Box>
          <IconButton className="redirect-btn">
            <ChevronRightRounded fontSize="large" />
          </IconButton>
        </Box>
        <Box className="category-info">
          <Typography>{getCategoryLabel(category?.name)}</Typography>
          {getCategoryLabel(category?.name) && gender && (
            <FiberManualRecordRounded className="name icon-size-6" />
          )}
          <Typography>{t(gender)}</Typography>
        </Box>
        <Box
          display="flex"
          flexDirection="row"
          gap="4px"
          justifyContent="space-between"
          alignItems="center"
          position="relative"
        >
          <Box className="status">{renderVoiceStatus(status)}</Box>
          {voiceInfoButtonAction()}
        </Box>
      </Box>
    </Box>
  );
};

const VoicesInformation = ({
  boxVoicesName,
  closeVoices,
  checkUserCanUseVoiceCloning,
  onCloseVoices,
  voices,
  onContinueRecord,
  onDeleteVoice,
  handleChangeVoiceData,
  handleClickGenerateVoice,
  onShowUpgradePackage,
  isShowTitle = true,
  isInstantVoiceCloning,
  onContinueUploadVoice,
  onDuplicateVoice,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { getFeatureValue } = useFeatureFlags();
  const user = useSelector((state) => state.auth.user);
  const { voiceId } = camelcaseKeys(queryString.parse(location.search) || {});

  const [openViewReason, setOpenViewReason] = useState(false);
  const [isShowContact, setIsShowContact] = useState(false);
  const [reasonContent, setReasonContent] = useState('');
  const [viewReasonId, setViewReasonId] = useState('');
  const [viewReasonVoiceStatus, setViewReasonVoiceStatus] = useState(null);
  const [viewReasonVoiceInfo, setViewReasonVoiceInfo] = useState(null);
  const [openVoiceDetail, setOpenVoiceDetail] = useState(false);

  // Warning dialog
  const [openWarningDialog, setOpenWarningDialog] = useState(false);
  const [warningTitle, setWarningTitle] = useState('');
  const [warningTextConfirm, setWarningTextConfirm] = useState('');
  const [warningTextConfirmValue, setWarningTextConfirmValue] = useState('');
  const [isShowWarningContent, setIsShowWarningContent] = useState(true);
  const [openProvideEmailDialog, setOpenProvideEmailDialog] = useState(false);

  const [stepVoiceDetail, setStepVoiceDetail] = useState(
    STEP_VOICE_DETAIL.VOICE_DETAIL,
  );

  const newUiItemVoiceCloning = getFeatureValue(
    FEATURE_KEYS.NEW_UI_ITEM_VOICE_CLONING,
    {
      userId: user.id,
      email: user.email,
    },
  );

  const duplicateVoiceEnabled = getFeatureValue(FEATURE_KEYS.DUPLICATE_VOICE, {
    userId: user.id,
    email: user.email,
  });

  const isDuplicateVoice =
    duplicateVoiceEnabled &&
    viewReasonVoiceStatus === VOICE_STATUS.FAILED &&
    viewReasonVoiceInfo?.type !== VOICE_CLONE_TYPE.ZERO_SHOT;

  const handleCloseWarningDialog = () => setOpenWarningDialog(false);

  const handleCloseViewReason = () => setOpenViewReason(false);

  const handleRecordAgain = () => {
    onContinueRecord(viewReasonId);
    setOpenViewReason(false);
  };

  const removeUrlParams = (name) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(name);
    history.push(url.pathname + url.search);
  };

  const scrollToVoice = (id) => {
    const element = document.getElementById(`voice-${id}`);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDeleteVoice = async (voice) => {
    // onClose();
    setWarningTitle(t('confirmDeleteVoice'));
    setWarningTextConfirm('confirmDeleteVoiceText');
    setWarningTextConfirmValue(voice);
    setIsShowWarningContent(false);
    setOpenWarningDialog(true);
  };

  useEffect(() => {
    if (voices.length > 0 && voiceId) {
      const voice = voices.find((v) => v.id === voiceId);
      if (voice) {
        dispatch(actions.voiceVoiceCloning.addVoice(voice));
        if (REASON_VOICE_STATUS.includes(voice.status)) {
          setReasonContent(voice.note);
          setOpenViewReason(true);
          setViewReasonId(voiceId);
          setIsShowContact(VOICE_NOT_USED.includes(voice.status));
          setViewReasonVoiceStatus(voice.status);
          setViewReasonVoiceInfo(voice);
          removeUrlParams('voiceId');
          scrollToVoice(voiceId);
          return;
        }

        if (VOICE_COMPLETED.includes(voice.status)) {
          setOpenVoiceDetail(true);
          removeUrlParams('voiceId');
          scrollToVoice(voiceId);
          return;
        }

        removeUrlParams('voiceId');
      }
    }
  }, [voices]);

  const renderVoiceDetail = () => (
    <VoiceDetail
      open={openVoiceDetail}
      onClose={() => setOpenVoiceDetail(false)}
      handleChangeVoiceData={handleChangeVoiceData}
      setWarningTitle={setWarningTitle}
      setWarningTextConfirm={setWarningTextConfirm}
      setOpenWarningDialog={setOpenWarningDialog}
      setWarningTextConfirmValue={setWarningTextConfirmValue}
      setIsShowWarningContent={setIsShowWarningContent}
      stepVoiceDetail={stepVoiceDetail}
      setStepVoiceDetail={setStepVoiceDetail}
    />
  );

  return (
    <StyledVoicesInformation>
      {isShowTitle && (
        <Box className="box-title-icon-btn">
          <Box className="box-title-icon-btn">
            <Typography className="title">{boxVoicesName}</Typography>
            <IconButton className="arrow-icon-btn" onClick={onCloseVoices}>
              {closeVoices ? (
                <KeyboardArrowDownRounded />
              ) : (
                <KeyboardArrowUpRounded />
              )}
            </IconButton>
          </Box>
        </Box>
      )}
      {!closeVoices && (
        <Box className="voices">
          {voices.map((item) =>
            newUiItemVoiceCloning ? (
              <VoiceItemCardNew
                key={item.id}
                id={item.id}
                avatar={item.avatar}
                name={item.name}
                locale={item.locale}
                category={item?.category}
                gender={item.gender}
                status={item.status}
                province={item?.province}
                subStatus={item?.subStatus}
                note={item?.note}
                voiceInfo={item}
                checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                setOpenVoiceDetail={setOpenVoiceDetail}
                setReasonContent={setReasonContent}
                setOpenViewReason={setOpenViewReason}
                setViewReasonId={setViewReasonId}
                setIsShowContact={setIsShowContact}
                setViewReasonVoiceStatus={setViewReasonVoiceStatus}
                setViewReasonVoiceInfo={setViewReasonVoiceInfo}
                onContinueRecord={onContinueRecord}
                onShowUpgradePackage={onShowUpgradePackage}
                handleDeleteVoice={handleDeleteVoice}
                isInstantVoiceCloning={isInstantVoiceCloning}
                setStepVoiceDetail={setStepVoiceDetail}
                onOpenProvideEmailDialog={setOpenProvideEmailDialog}
                onContinueUploadVoice={onContinueUploadVoice}
                onDuplicateVoice={onDuplicateVoice}
              />
            ) : (
              <VoiceInformation
                key={item.id}
                id={item.id}
                avatar={item.avatar}
                name={item.name}
                locale={item.locale}
                category={item?.category}
                gender={item.gender}
                status={item.status}
                province={item?.province}
                subStatus={item?.subStatus}
                note={item?.note}
                voiceInfo={item}
                checkUserCanUseVoiceCloning={checkUserCanUseVoiceCloning}
                setOpenVoiceDetail={setOpenVoiceDetail}
                setReasonContent={setReasonContent}
                setOpenViewReason={setOpenViewReason}
                setViewReasonId={setViewReasonId}
                setIsShowContact={setIsShowContact}
                setViewReasonVoiceStatus={setViewReasonVoiceStatus}
                setViewReasonVoiceInfo={setViewReasonVoiceInfo}
                onContinueRecord={onContinueRecord}
                onShowUpgradePackage={onShowUpgradePackage}
                handleDeleteVoice={handleDeleteVoice}
                isInstantVoiceCloning={isInstantVoiceCloning}
                setStepVoiceDetail={setStepVoiceDetail}
                onOpenProvideEmailDialog={setOpenProvideEmailDialog}
                onContinueUploadVoice={onContinueUploadVoice}
              />
            ),
          )}
        </Box>
      )}
      {newUiItemVoiceCloning ? (
        <ViewReasonNew
          open={openViewReason}
          note={reasonContent}
          handleClickGenerateVoice={handleClickGenerateVoice}
          onClose={handleCloseViewReason}
          voiceStatus={viewReasonVoiceStatus}
          onDuplicateVoice={() => {
            if (!isDuplicateVoice && viewReasonVoiceInfo) {
              dispatch(actions.voiceVoiceCloning.addVoice(viewReasonVoiceInfo));
              history.push(route.VOICE_CLONING_CREATE);
            }

            if (isDuplicateVoice && onDuplicateVoice) {
              onDuplicateVoice(viewReasonVoiceInfo.id);
            }
          }}
          isDuplicateVoice={isDuplicateVoice}
        />
      ) : (
        <ViewReason
          viewReasonId={viewReasonId}
          open={openViewReason}
          note={reasonContent}
          onClose={handleCloseViewReason}
          handleRecordAgain={handleRecordAgain}
          isShowContact={isShowContact}
          handleClickGenerateVoice={handleClickGenerateVoice}
        />
      )}
      {openVoiceDetail && renderVoiceDetail()}
      <WarningDialog
        open={openWarningDialog}
        title={warningTitle}
        warningTextConfirm={warningTextConfirm}
        warningTextConfirmValue={warningTextConfirmValue}
        onClose={handleCloseWarningDialog}
        handleChangeVoiceData={handleChangeVoiceData}
        onDeleteVoice={onDeleteVoice}
        showWarningContent={isShowWarningContent}
      />
      {openProvideEmailDialog && (
        <ProvideEmailToUseFeatureDialog
          onClose={() => setOpenProvideEmailDialog(false)}
        />
      )}
    </StyledVoicesInformation>
  );
};

export default VoicesInformation;
